import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Plus, Trash2, Edit3, Eye, EyeOff, Loader2, Scale, Calendar, Video, X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Property Development & Conveyancing",
  "Notarial Services",
  "Commercial Agreements",
  "Civil Litigation",
  "Corporate Governance",
];

const emptyForm = {
  title: "",
  practical_question: "",
  what_happened: "",
  court_decision: "",
  why_it_matters: "",
  actions_to_consider: "",
  judgment_date: "",
  source: "",
  related_service: "",
  video_url: "",
  cover_image: "",
  published: true,
};

export default function AdminCaseWatchPage() {
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => { setUser(res.data); setAuthChecking(false); })
      .catch(() => { navigate("/admin/login"); });
  }, [navigate]);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/case-watch`);
      setCases(res.data);
    } catch { toast.error("Failed to load cases"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) fetchCases(); }, [user, fetchCases]);

  const openNew = () => { setEditingId(null); setForm({ ...emptyForm }); setShowForm(true); };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      title: c.title,
      practical_question: c.practical_question,
      what_happened: c.what_happened,
      court_decision: c.court_decision,
      why_it_matters: c.why_it_matters,
      actions_to_consider: c.actions_to_consider,
      judgment_date: c.judgment_date,
      source: c.source,
      related_service: c.related_service,
      video_url: c.video_url || "",
      cover_image: c.cover_image || "",
      published: c.published,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.practical_question || !form.judgment_date || !form.related_service) {
      toast.error("Please fill in title, practical question, judgment date and related service");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, video_url: form.video_url || null, cover_image: form.cover_image || null };
      if (editingId) {
        await axios.put(`${API}/case-watch/${editingId}`, payload, { withCredentials: true });
        toast.success("Case updated");
      } else {
        await axios.post(`${API}/case-watch`, payload, { withCredentials: true });
        toast.success("Case created");
      }
      setShowForm(false);
      fetchCases();
    } catch (err) {
      toast.error("Failed to save case");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/case-watch/${id}`, { withCredentials: true });
      toast.success("Case deleted");
      setDeleteConfirm(null);
      fetchCases();
    } catch { toast.error("Failed to delete"); }
  };

  if (authChecking) {
    return <div className="min-h-screen bg-charcoal flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-gold" /></div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Manage Case Watch" description="Admin Case Watch management" />
      <Header />

      <section className="pt-32 pb-12 bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-3">Admin Panel</p>
              <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-white tracking-tighter">Manage Case Watch</h1>
            </div>
            <Button onClick={openNew} className="btn-gold px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-none flex items-center gap-2" data-testid="add-case-btn">
              <Plus className="h-4 w-4" /> New Case
            </Button>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand-gold" /></div>
          ) : cases.length === 0 ? (
            <div className="text-center py-20">
              <Scale className="h-16 w-16 mx-auto text-charcoal/20 mb-4" />
              <p className="text-charcoal/50 mb-6">No case watch entries yet.</p>
              <Button onClick={openNew} className="btn-gold rounded-none" data-testid="empty-add-case-btn"><Plus className="h-4 w-4 mr-2" /> Create Case</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {cases.map((c) => (
                <motion.div key={c.id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4 p-4 border border-charcoal/10 hover:border-brand-gold/30 transition-colors" data-testid={`admin-case-${c.id}`}>
                  <Scale className="h-10 w-10 text-brand-gold/40 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg text-charcoal truncate">{c.title}</h3>
                    <div className="flex items-center gap-3 mt-1 text-sm text-charcoal/50">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{c.judgment_date}</span>
                      <span className="text-brand-gold">{c.related_service}</span>
                      {c.video_url && <span className="flex items-center gap-1 text-blue-500"><Video className="h-3 w-3" />Video</span>}
                      {c.published ? <span className="flex items-center gap-1 text-green-600"><Eye className="h-3 w-3" />Published</span> : <span className="flex items-center gap-1 text-amber-600"><EyeOff className="h-3 w-3" />Draft</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/case-watch/${c.id}`)} className="text-charcoal/50 hover:text-brand-gold"><Eye className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} className="text-charcoal/50 hover:text-brand-gold" data-testid={`edit-case-${c.id}`}><Edit3 className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(c.id)} className="text-charcoal/50 hover:text-red-600" data-testid={`delete-case-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editingId ? "Edit Case" : "New Case Watch Entry"}</DialogTitle>
            <DialogDescription className="text-charcoal/50 text-sm">Upload a court judgment summary with video.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Can a Seller Cancel a Property Sale?" className="rounded-none mt-1" data-testid="case-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Judgment Date *</Label>
                <Input type="date" value={form.judgment_date} onChange={(e) => setForm({ ...form, judgment_date: e.target.value })} className="rounded-none mt-1" data-testid="case-date-input" />
              </div>
              <div>
                <Label>Related Service *</Label>
                <Select value={form.related_service} onValueChange={(v) => setForm({ ...form, related_service: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="case-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent className="bg-white">{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>The Practical Question *</Label>
              <Textarea value={form.practical_question} onChange={(e) => setForm({ ...form, practical_question: e.target.value })} placeholder="The key question this case addresses..." rows={2} className="rounded-none mt-1" data-testid="case-question" />
            </div>
            <div>
              <Label>What Happened</Label>
              <Textarea value={form.what_happened} onChange={(e) => setForm({ ...form, what_happened: e.target.value })} placeholder="Brief facts of the case..." rows={3} className="rounded-none mt-1" data-testid="case-happened" />
            </div>
            <div>
              <Label>What the Court Decided</Label>
              <Textarea value={form.court_decision} onChange={(e) => setForm({ ...form, court_decision: e.target.value })} placeholder="The court's ruling..." rows={3} className="rounded-none mt-1" data-testid="case-decision" />
            </div>
            <div>
              <Label>Why It Matters</Label>
              <Textarea value={form.why_it_matters} onChange={(e) => setForm({ ...form, why_it_matters: e.target.value })} placeholder="Practical implications for clients..." rows={3} className="rounded-none mt-1" data-testid="case-matters" />
            </div>
            <div>
              <Label>Actions Clients Should Consider</Label>
              <Textarea value={form.actions_to_consider} onChange={(e) => setForm({ ...form, actions_to_consider: e.target.value })} placeholder="Recommended next steps..." rows={3} className="rounded-none mt-1" data-testid="case-actions" />
            </div>
            <div>
              <Label>Source</Label>
              <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="e.g. Supreme Court of Appeal — Case No 123/2026" className="rounded-none mt-1" data-testid="case-source" />
            </div>
            <div>
              <Label>Video URL (YouTube/Vimeo embed)</Label>
              <Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://www.youtube.com/embed/..." className="rounded-none mt-1" data-testid="case-video-url" />
              <p className="text-charcoal/40 text-xs mt-1">Use the embed URL from YouTube or Vimeo "Share &gt; Embed"</p>
            </div>
            <div>
              <Label>Cover Image URL (optional)</Label>
              <Input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="https://..." className="rounded-none mt-1" data-testid="case-cover-image" />
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setForm({ ...form, published: !form.published })} className={`relative w-11 h-6 rounded-full transition-colors ${form.published ? "bg-brand-gold" : "bg-charcoal/20"}`} data-testid="case-published-toggle">
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.published ? "translate-x-5" : ""}`} />
              </button>
              <span className="text-sm text-charcoal/70">{form.published ? "Published" : "Draft"}</span>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowForm(false)} className="rounded-none">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-gold rounded-none px-8" data-testid="save-case-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingId ? "Update Case" : "Create Case"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Delete Case?</DialogTitle>
            <DialogDescription className="sr-only">Confirm deletion</DialogDescription>
          </DialogHeader>
          <p className="text-charcoal/60 text-sm py-4">This action cannot be undone.</p>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="rounded-none">Cancel</Button>
            <Button onClick={() => handleDelete(deleteConfirm)} className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="confirm-delete-case-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
